/**
 * Product-level feed behavior rules from the user perspective.
 * Declarative only — no ranking formulas or algorithms.
 */
export interface FeedBehaviorRule {
  readonly id: string;
  readonly rule: string;
  readonly userFacingEffect: string;
}

export const FEED_BEHAVIOR_RULES: readonly FeedBehaviorRule[] = [
  {
    id: 'new_creator_boost',
    rule: 'New creators appear with boosted visibility in the public feed.',
    userFacingEffect:
      'Recently onboarded creators are more likely to appear near the top so audiences can discover fresh voices.',
  },
  {
    id: 'monetizing_creator_weight',
    rule: 'Monetizing creators receive higher distribution weight.',
    userFacingEffect:
      'Creators who sell products or earn revenue are surfaced more often because they contribute to the creator economy.',
  },
  {
    id: 'engagement_increases_exposure',
    rule: 'Engagement increases feed ranking exposure.',
    userFacingEffect:
      'Posts and creators that receive likes, follows, and repeat views stay visible longer and reappear more frequently.',
  },
  {
    id: 'inactive_creator_decay',
    rule: 'Inactive creators decay in visibility over time.',
    userFacingEffect:
      'Creators who stop publishing or engaging gradually appear less often until they become active again.',
  },
  {
    id: 'published_only',
    rule: 'Only published content from active creators appears in the feed.',
    userFacingEffect: 'Users never see drafts, hidden posts, or content from suspended creators.',
  },
  {
    id: 'chronological_baseline',
    rule: 'The feed defaults to chronological ordering before behavior adjustments.',
    userFacingEffect: 'Users experience a timely stream, with boosts applied on top of recency.',
  },
  {
    id: 'followed_creator_priority',
    rule: 'Followed creators receive priority in the personal feed context.',
    userFacingEffect: 'After following someone, their posts appear more consistently in your feed.',
  },
];

export type FeedBehaviorRules = typeof FEED_BEHAVIOR_RULES;
